//
import { getSecondLineIcon, parseDate } from "shared/search/inline-selectors/inline-selectors"
import {
    checkIfHtml,
    checkLineBreakHtml,
    generateStaticUrl,
    simpleLineBreak,
    stringContainsHTMLMarkup,
} from "shared/imports/sharedHelperFunctions"

import { BLANK_AVATAR_PATH } from "shared/imports/sharedConstants"

import { YoutubeInlineRightSection } from "@/pages/search/YoutubeInlineRightSection"

const shouldShowRegion = (datum) =>
    Userdata.isStatesMode() &&
    !Userdata.isSingleStateMode() &&
    datum.get("source_type") !== DjangIO.app.document.models.DocumentSourceType.custom_contact.value &&
    !datum.get("source_custom_organization")

const defaultDocumentInlineSelector = (datum) => {
    let firstLineIsHtml = false
    const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
    const snippets = datum.get("snippets")
    const firstLine = datum.get("title")

    if (stringContainsHTMLMarkup(firstLine)) {
        firstLineIsHtml = true
    }

    const getSecondLine = () => {
        const sourceName = datum.get("source_name")
        if (sourceName && sourceName !== "No Source") {
            return `${documentTypeEnum.label} from ${datum.get("source_name")} on ${parseDate(datum.get("date"))}`
        } else {
            return `${documentTypeEnum.label} on ${parseDate(datum.get("date"))}`
        }
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (documentTypeEnum) {
            secondLine.push({
                ...getSecondLineIcon("type"),
                value: documentTypeEnum.label,
            })
        }

        const sourceName = datum.get("source_name")
        if (sourceName && sourceName !== "No Source") {
            const sourceTypeEnum = DjangIO.app.document.models.DocumentSourceType.by_value(datum.get("source_type"))

            secondLine.push({
                fa5Icon: sourceTypeEnum.fa5_icon,
                fa5IconFamily: sourceTypeEnum.fa5_icon_family,
                value: sourceName,
            })
        }

        return secondLine
    }

    return {
        href: `/document/${datum.get("id")}`,
        iconName: documentTypeEnum.icon.replace("fa-", ""),
        model: DjangIO.app.models.QuorumDataType.document.value,
        pdfUrl: datum.get("pdf_url"),
        region: DjangIO.app.models.Region.by_value(datum.get("region")).region_name,
        showRegion: shouldShowRegion(datum),
        firstLineData: firstLine,
        secondLineData: getSecondLine(),
        thirdLineData: datum.get("content"),
        firstLineIsHtml,
        snippets: datum.get("pdf_url") && snippets && snippets.size > 0 ? snippets.toJS() : undefined,

        secondLineDS: getSecondLineDS(),
    }
}

const getSocialFirstLine = (datum) => {
    const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))

    return datum.get("source_name") === "No Source"
        ? documentTypeEnum.label
        : `${documentTypeEnum.label} from ${datum.get("source_name")}`
}

const getSocialSelector = (datum) => ({
    ...defaultDocumentInlineSelector(datum),
    region: DjangIO.app.models.Region.by_value(datum.get("region")).region_name,
    firstLineData: getSocialFirstLine(datum),
    secondLineData: defaultDocumentInlineSelector(datum).secondLineData,
    thirdLineData: datum.get("content"),
    thirdLineIsHtml: true,
    websiteUrl: datum.get("source_url"),
})

const stateBillSupportingDocInlineSelector = (datum) => {
    const getSecondLine = () => {
        let label
        if (datum.get("document_subtype")) {
            // Assignment without declaration destructuring syntax
            ;({ label } = DjangIO.app.bill.models.StateBillDocumentType.by_value(datum.get("document_subtype")))
        } else {
            const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
            const region = DjangIO.app.models.Region.by_value(datum.get("region")).region_name
            label = `${region} ${documentTypeEnum.label.replace("State ", "")}`
        }

        const date = parseDate(datum.get("date"))
        return `${label} on ${date}`
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (datum.get("document_subtype")) {
            secondLine.push({
                value: DjangIO.app.bill.models.StateBillDocumentType.by_value(datum.get("document_subtype")).label,
            })
        } else {
            const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
            const region = DjangIO.app.models.Region.by_value(datum.get("region")).region_name

            secondLine.push({
                value: `${region} ${documentTypeEnum.label.replace("State ", "")}`,
            })
        }

        return secondLine
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        secondLineData: getSecondLine(),
        thirdLineData: datum.get("raw_content"),
        websiteUrl: datum.get("source_url"),

        secondLineDS: getSecondLineDS(),
    }
}

const gaoReportInlineSelector = (datum) => {
    const getSecondLine = () => {
        const { label } = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
        const date = parseDate(datum.get("date"))
        return `${label} on ${date}`
    }

    const { newLine: thirdLine, isHtml: thirdLineIsHtml } = checkIfHtml(datum.get("summary"))

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        secondLine.push({
            ...getSecondLineIcon("type"),
            value: DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type")).label,
        })

        return secondLine
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        secondLineData: getSecondLine(),
        thirdLineData: thirdLine,
        websiteUrl: datum.get("source_url"),
        thirdLineIsHtml,

        secondLineDS: getSecondLineDS(),
    }
}

const crsReportInlineSelector = (datum) => {
    const getSecondLine = () => {
        const { label } = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
        const externalPK = datum.get("external_pk")
        const date = parseDate(datum.get("date"))

        return `${label} ${externalPK} on ${date}`
    }

    const { newLine: thirdLine, isHtml: thirdLineIsHtml } = checkIfHtml(datum.get("summary"))

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        const { label } = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
        const externalPK = datum.get("external_pk")
        secondLine.push({
            ...getSecondLineIcon("type"),
            value: `${label} ${externalPK}`,
        })

        return secondLine
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        secondLineData: getSecondLine(),
        thirdLineData: thirdLine,
        websiteUrl: datum.get("source_url"),
        thirdLineIsHtml,

        secondLineDS: getSecondLineDS(),
    }
}

const cleanUpJournalists = (journalists) => {
    if (typeof journalists !== "string") {
        journalists = JSON.stringify(journalists)
    }
    if (journalists) {
        journalists = journalists
            ?.replace(/u'/g, "'")
            .replace(/(?<!\\)'/g, '"')
            .replace(/""/g, '"')
            .replace(/\\/g, "\\\\")
        journalists = JSON.parse(journalists)
        if (Array.isArray(journalists) && journalists.length) {
            journalists = journalists?.map((journalist) =>
                JSON.parse('"' + journalist.replace(/\\/g, "\\\\") + '"').replace(/\\/g, ""),
            )
        } else {
            return undefined
        }
    }
    return journalists
}

const axiosArticleInlineSelector = (datum) => {
    const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))
    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (documentTypeEnum) {
            secondLine.push({
                ...getSecondLineIcon("type"),
                value: documentTypeEnum.label,
            })
        }

        const sourceName = datum.get("source_name")
        if (sourceName && sourceName !== "No Source") {
            const sourceTypeEnum = DjangIO.app.document.models.DocumentSourceType.by_value(datum.get("source_type"))

            secondLine.push({
                fa5Icon: sourceTypeEnum.fa5_icon,
                fa5IconFamily: sourceTypeEnum.fa5_icon_family,
                value: sourceName,
            })
        }

        // Add journalists and issue area to the second line data
        let journalists = cleanUpJournalists(datum.get("journalists"))
        // This check is required so we don't add just the journalist icon if there are no journalists
        if (journalists !== undefined) {
            secondLine.push({
                ...getSecondLineIcon("journalists"),
                value: journalists?.join(", "),
            })
        }
        if (datum.get("issue_area")) {
            secondLine.push({
                ...getSecondLineIcon("issue-area"),
                value: datum.get("issue_area"),
            })
        }
        return secondLine
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        thirdLineIsHtml: true,
        secondLineDS: getSecondLineDS(),
    }
}

const cboReportInlineSelector = (datum) => {
    const { newLine: thirdLine, isHtml: thirdLineIsHtml } = checkIfHtml(datum.get("summary"))

    return {
        ...defaultDocumentInlineSelector(datum),
        thirdLineData: thirdLine,
        websiteUrl: datum.get("source_url"),
        thirdLineIsHtml,
    }
}

const committeeDocumentInlineSelector = (datum) => {
    const getSecondLine = () => {
        const date = parseDate(datum.get("date"))
        if (datum.get("source_committee_name")) {
            return `${datum.get("source_committee_name")} Hearing Document on ${date}`
        }

        if (datum.get("source_name") === "No Source") {
            return `Document for Committee Hearing on ${date}`
        } else {
            return `Document for Committee Hearing from ${datum.get("source_name")} on ${date}`
        }
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        secondLine.push({
            ...getSecondLineIcon("type"),
            value: `Document for Committee Hearing`,
        })

        const sourceName = datum.get("source_name")
        if (sourceName && sourceName !== "No Source") {
            secondLine.push({
                ...getSecondLineIcon("gavel"),
                value: sourceName,
            })
        } else if (datum.get("source_committee_name")) {
            secondLine.push({
                ...getSecondLineIcon("gavel"),
                value: datum.get("source_committee_name"),
            })
        }

        return secondLine
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        secondLineData: getSecondLine(),
        websiteUrl: datum.get("see_more_url"),

        secondLineDS: getSecondLineDS(),
    }
}

const committeeReportInlineSelector = (datum) => ({
    ...defaultDocumentInlineSelector(datum),
    websiteUrl: datum.get("source_url"),
})

const whiteHouseInlineSelector = (datum) => {
    const text = datum.get("content")
    let thirdLineIsHtml = false

    const getThirdLine = () => {
        // executive orders contain a mix of '\n' and <br>
        if (stringContainsHTMLMarkup(text) || text.includes("\n")) {
            thirdLineIsHtml = true
            return simpleLineBreak(text)
        }
        return text
    }

    return {
        ...defaultDocumentInlineSelector(datum),
        thirdLineData: getThirdLine(),
        websiteUrl: datum.get("source_url"),
        thirdLineIsHtml,
    }
}

const dearColleagueLetterInlineSelector = (datum) => {
    let thirdLineIsHtml = false

    const getThirdLine = () => {
        const text = datum.get("content")

        if (stringContainsHTMLMarkup(text)) {
            thirdLineIsHtml = true
        }

        return text
    }

    return { ...defaultDocumentInlineSelector(datum), thirdLineData: getThirdLine(), thirdLineIsHtml }
}

const constituentEmailInlineSelector = (datum) => {
    let dangerouslyInsertThirdLine = false

    const getThirdLine = () => {
        const text = datum.get("content")

        if (text && stringContainsHTMLMarkup(text)) {
            dangerouslyInsertThirdLine = true
        }

        return text
    }

    return { ...defaultDocumentInlineSelector(datum), thirdLineData: getThirdLine(), dangerouslyInsertThirdLine }
}

// SOCIAL MEDIA (TWEET, INSTA, FB) SELECTORS
const tweetInlineSelector = (datum) => {
    const getSecondLine = () => {
        const detailArray = [parseDate(datum.get("date"))]
        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            detailArray.push("Campaign Account")
        }
        if (datum.get("retweet_count")) {
            detailArray.push(`${datum.get("retweet_count")} Retweets`)
        }
        if (datum.get("favorite_count")) {
            detailArray.push(`${datum.get("favorite_count")} Favorites`)
        }
        return detailArray.join(" | ")
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            secondLine.push({
                value: "Campaign Account",
            })
        }
        if (datum.get("retweet_count")) {
            secondLine.push({
                value: `${datum.get("retweet_count")} Retweets`,
            })
        }
        if (datum.get("favorite_count")) {
            secondLine.push({
                value: `${datum.get("favorite_count")} Favorites`,
            })
        }

        return secondLine
    }

    return {
        ...getSocialSelector(datum),

        // update the icon to use X icon
        iconName: null,
        fa5IconName: "x-twitter",
        fa5IconFamily: "q",

        secondLineData: getSecondLine(),

        secondLineDS: getSecondLineDS(),
    }
}

const facebookPostInlineSelector = (datum) => {
    const getSecondLine = () => {
        const detailArray = [parseDate(datum.get("date"))]
        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            detailArray.push("Campaign Account")
        }
        if (datum.get("like_count")) {
            detailArray.push(`${datum.get("like_count")} Likes`)
        }
        if (datum.get("share_count")) {
            detailArray.push(`${datum.get("share_count")} Shares`)
        }
        return detailArray.join(" | ")
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            secondLine.push({
                value: "Campaign Account",
            })
        }
        if (datum.get("like_count")) {
            secondLine.push({
                ...getSecondLineIcon("thumbs-up"),
                value: `${datum.get("like_count")} Likes`,
            })
        }
        if (datum.get("share_count")) {
            secondLine.push({
                ...getSecondLineIcon("share"),
                value: `${datum.get("share_count")} Shares`,
            })
        }

        return secondLine
    }

    return {
        ...getSocialSelector(datum),
        secondLineData: getSecondLine(),
        thirdLineData: simpleLineBreak(datum.get("content")),

        secondLineDS: getSecondLineDS(),
    }
}

const mediumPostInlineSelector = (datum) => ({
    ...getSocialSelector(datum),
    firstLineData: defaultDocumentInlineSelector(datum).firstLineData,
})

const instagramPostInlineSelector = (datum) => {
    const getSecondLine = () => {
        const detailArray = [parseDate(datum.get("date"))]
        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            detailArray.push("Campaign Account")
        }
        if (datum.get("like_count")) {
            detailArray.push(`${datum.get("like_count")} Likes`)
        }
        return detailArray.join(" | ")
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        if (datum.get("document_subtype") === DjangIO.app.statements.models.PressReleaseType.Campaign.value) {
            secondLine.push({
                value: "Campaign Account",
            })
        }
        if (datum.get("like_count")) {
            secondLine.push({
                value: `${datum.get("like_count")} Likes`,
            })
        }

        return secondLine
    }

    return {
        ...getSocialSelector(datum),
        secondLineData: getSecondLine(),

        secondLineDS: getSecondLineDS(),
    }
}

const youtubeInlineSelector = (datum) => {
    const getSecondLine = () => {
        const secondLine = []

        const title = defaultDocumentInlineSelector(datum).firstLineData
        if (title) {
            secondLine.push(title)
        }

        const date = datum.get("date")
        if (date) {
            secondLine.push(parseDate(date))
        }

        return secondLine.join(" | ")
    }

    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        const title = defaultDocumentInlineSelector(datum).firstLineData
        if (title) {
            secondLine.push({
                value: title,
            })
        }

        return secondLine
    }

    return {
        ...getSocialSelector(datum),
        rightSection: {
            component: YoutubeInlineRightSection,
        },
        customThirdLineClass: "youtube-third-line",
        firstLineData: getSocialSelector(datum).firstLineData,
        secondLineData: getSecondLine(),
        thirdLineData: null,

        secondLineDS: getSecondLineDS(),
    }
}

// eu selectors
const euInlineSelector = (datum) => {
    const { newLine: thirdLine, isHtml: thirdLineIsHtml } = checkLineBreakHtml(datum.get("content"))

    return { ...defaultDocumentInlineSelector(datum), thirdLineData: thirdLine, thirdLineIsHtml }
}

const committeeTranscriptInlineSelector = (datum) => {
    let hasNoProfile = false
    let secondLineIsHtml = false

    const documentTypeEnum = DjangIO.app.document.models.DocumentType.by_value(datum.get("document_type"))

    const getFirstLine = () => {
        const firstLine = ["Statement in Hearing"]

        const getSpeakerName = () => {
            const sourceName = datum.get("source_name")
            const speakerFullName = datum.get("speaker_full_name")
            const speakerName = datum.get("speaker_name")

            if (sourceName !== "No Source") {
                return sourceName
            } else if (speakerFullName) {
                return speakerFullName
            } else if (speakerName) {
                return speakerName
            }

            return undefined
        }

        const speakerName = getSpeakerName()
        if (speakerName) {
            firstLine.push(`by ${speakerName}`)
        }

        return firstLine.join(" ")
    }

    const getSecondLine = () => {
        const secondLine = []
        const committeeInfo = []

        const eventId = datum.get("committee_meeting_id")
        const eventName = datum.getIn(["_extra", "committee_meeting", "committee_meeting__subject"])
        if (eventId && eventName) {
            secondLineIsHtml = true
            committeeInfo.push(`<a href="/event/${eventId}/">${eventName}</a>`)
        }

        const committeeId = datum.getIn(["_extra", "committee_meeting", "committee_meeting__source_committee_id"])
        const committeeName = datum.getIn(["_extra", "committee_meeting", "committee_meeting__source_committee__name"])
        if (committeeId && committeeName) {
            secondLineIsHtml = true
            committeeInfo.push(`<a href="/committee/${committeeId}/">${committeeName}</a>`)
        }

        if (committeeInfo.length) {
            secondLine.push(committeeInfo.join(" from "))
        }

        const dateString = parseDate(datum.get("date"))
        if (dateString) {
            secondLine.push(`on ${dateString}`)
        }

        return secondLine.join(" ")
    }

    const { newLine: thirdLine, isHtml: thirdLineIsHtml } = checkLineBreakHtml(datum.get("content"))

    const committeeMeetingId = datum.get("committee_meeting_id")
    if (!committeeMeetingId) {
        hasNoProfile = true
    }

    const href = !hasNoProfile
        ? `/event/${committeeMeetingId}/transcript/?sequence=${datum.get("sequence")}`
        : undefined

    const mostRecentParty = datum.getIn(["_extra", "official_image", "source_person__most_recent_party"])

    let secondLineDSIsHtml = false
    const getSecondLineDS = () => {
        const secondLine = []

        if (datum.get("date")) {
            secondLine.push({
                ...getSecondLineIcon("date"),
                value: parseDate(datum.get("date")),
            })
        }

        const committeeInfo = []

        const eventId = datum.get("committee_meeting_id")
        const eventName = datum.getIn(["_extra", "committee_meeting", "committee_meeting__subject"])
        if (eventId && eventName) {
            secondLineDSIsHtml = true
            committeeInfo.push(`<a href="/event/${eventId}/">${eventName}</a>`)
        }

        const committeeId = datum.getIn(["_extra", "committee_meeting", "committee_meeting__source_committee_id"])
        const committeeName = datum.getIn(["_extra", "committee_meeting", "committee_meeting__source_committee__name"])
        if (committeeId && committeeName) {
            secondLineDSIsHtml = true
            committeeInfo.push(`<a href="/committee/${committeeId}/">${committeeName}</a>`)
        }

        if (committeeInfo.length) {
            secondLine.push({
                value: committeeInfo.join(" from "),
            })
        }

        return secondLine
    }

    return {
        hasNoProfile,
        href,
        iconName: documentTypeEnum.icon.replace("fa-", ""),
        imagePath:
            datum.getIn(["_extra", "official_image", "source_person__image_url"]) ||
            generateStaticUrl(BLANK_AVATAR_PATH),
        imageBorderRule:
            mostRecentParty && DjangIO.app.person.types.Party.by_value(mostRecentParty).class_name.toLowerCase(),
        model: DjangIO.app.models.QuorumDataType.document.value,
        pdfUrl: datum.get("pdf_url"),
        region: DjangIO.app.models.Region.by_value(datum.get("region")).region_name,
        showRegion: shouldShowRegion(datum),
        firstLineData: getFirstLine(),
        secondLineData: getSecondLine(),
        thirdLineData: thirdLine,
        secondLineIsHtml,
        thirdLineIsHtml,

        secondLineDS: getSecondLineDS(),
        secondLineDSIsHtml,
    }
}

const documentInlineSelector = (datum) => {
    switch (datum.get("document_type")) {
        // social selectors
        case DjangIO.app.document.models.DocumentType.tweet.value:
            return tweetInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.facebook_post.value:
            return facebookPostInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.instagram_post.value:
            return instagramPostInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.medium_post.value:
            return mediumPostInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.youtube_video.value:
            return youtubeInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.state_bill_supporting_document.value:
            return stateBillSupportingDocInlineSelector(datum)
        // report selectors
        case DjangIO.app.document.models.DocumentType.gao_report.value:
            return gaoReportInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.crs_report.value:
            return crsReportInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.cbo_report.value:
            return cboReportInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.committee_report.value:
            return committeeReportInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.committee_document.value:
            return committeeDocumentInlineSelector(datum)
        // executive selectors
        case DjangIO.app.document.models.DocumentType.executive_order.value:
        case DjangIO.app.document.models.DocumentType.press_briefing.value:
        case DjangIO.app.document.models.DocumentType.proclamation.value:
        case DjangIO.app.document.models.DocumentType.memorandum.value:
        case DjangIO.app.document.models.DocumentType.nomination.value:
        case DjangIO.app.document.models.DocumentType.press_release.value:
        case DjangIO.app.document.models.DocumentType.floor_statement.value:
        case DjangIO.app.document.models.DocumentType.omb_sap.value:
            return whiteHouseInlineSelector(datum)
        // email/letter (containing external html content) selectors
        case DjangIO.app.document.models.DocumentType.constituent_email.value:
            return constituentEmailInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.dc_letter.value:
            return dearColleagueLetterInlineSelector(datum)
        // eu selectors
        case DjangIO.app.document.models.DocumentType.speech.value:
        case DjangIO.app.document.models.DocumentType.parliamentary_question.value:
        case DjangIO.app.document.models.DocumentType.vote_explanation.value:
        case DjangIO.app.document.models.DocumentType.motion_for_resolution.value:
        case DjangIO.app.document.models.DocumentType.speech_in_plenary.value:
            return euInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.committee_transcript.value:
        case DjangIO.app.document.models.DocumentType.manual_committee_transcript.value:
        case DjangIO.app.document.models.DocumentType.us_president_transcript.value:
        case DjangIO.app.document.models.DocumentType.state_floor_transcript.value:
        case DjangIO.app.document.models.DocumentType.local_meeting_transcript.value:
            return committeeTranscriptInlineSelector(datum)
        case DjangIO.app.document.models.DocumentType.axios_article_free.value:
        case DjangIO.app.document.models.DocumentType.axios_article_pro.value:
            return axiosArticleInlineSelector(datum)
        default:
            return defaultDocumentInlineSelector(datum)
    }
}

export default documentInlineSelector
